import { Env } from "~/types";

export var env = import.meta.env.VITE_ENV;

const validEnvs = ["Prod", "Preprod", "Staging", "Local"] as const;

if (process.env.LOCAL === "1") {
  env = "Local";
}

if (!env) {
  env = "Staging";
}

if (!validEnvs.includes(env as Env)) {
  throw new Error(`Invalid env: ${env}`);
}

type Status = "enabled" | "disabled";

type paymentGateway = "razorpay" | "custom";

const ENABLE_MIDDLEWARE_LOGS: Status =
  process.env.ENABLE_MIDDLEWARE_LOGS === "1" ? "enabled" : "disabled";

export type Config = {
  env: Env;
  baseUrl: string;
  baseDomainUrl: string;
  receiptUrl: string;
  enableErrorLoggingToSentry: boolean;
  razorpayKey: string;
  isRazorpayEnabled: boolean;
  bannerPath: string;
  showErrorMessageInErrorView: boolean;
  clientBannerBasePath: string;
  clientBannerFileName: string;
  paths: {
    homePageUrl: string;
    categoryPageBasePath: string;
  };
  consoleLogging: {
    router: Status;
    fraudChecks: Status;
    serverRequests: Status;
    sdk: Status;
    middleware: Status;
    events: Status;
  };
  uiLogging: {
    loaderFromContext: Status;
  };
  clientIdPrefix: Record<string, string>;
  mixpanelEvents: {
    enabledForWebStore: boolean;
    enabledForSdk: boolean;
    enabledForRnr: boolean;
  };
  axisClientId: string;
  tdClientId: string;
  tdSubscriptionRazorpayKey: string;
  customizedSDKClients: {
    limitedCategoryContent: string[];
    disableHomePageBackButton: string[];
    consumeCoinsByDefault: string[];
    squareAssetsForPopularBrands: string[];
    lowercaseTitleText: string[];
    trimCoinsOnAppHeader: string[];
  };
  donationPartner: string[];
  payUUrl: string;
  sesssionDurationInMinutes: {
    webstore: number;
    rnr: number;
    squid: number;
    gift_receiver: number;
    code_base_rnr_portal: number;
    pluto: number;
    smart_greeting: number; // sender's end
    tp_cpn: number;
  };
  webstore: {
    paymentGateway: paymentGateway;
  };
  isCommsEnabled: boolean;
};

const defaultConfig = {
  consoleLogging: {
    router: "disabled" as Status,
    fraudChecks: "disabled" as Status,
    serverRequests: "disabled" as Status,
    sdk: "disabled" as Status,
    middleware: ENABLE_MIDDLEWARE_LOGS,
    events: "disabled" as Status,
  },
  uiLogging: {
    loaderFromContext: "disabled" as Status,
  },
  paths: {
    homePageUrl: "/",
    categoryPageBasePath: "/shop-by-category/",
  },
  clientIdPrefix: {
    onepercent: "onepercent",
    visitHealth: "visit-health",
    gapup: "gapup",
    raisoni: "raisoni",
    td: "tata-digital",
    kotak: "kotak",
  },
  clientBannerBasePath: "partners",
  clientBannerFileName: "home_banners.json",
  isRazorpayEnabled: false,
  mixpanelEvents: {
    enabledForWebStore: false,
    enabledForSdk: false,
    enabledForRnr: false,
  },
  donationPartner: ["ketto"],
  sesssionDurationInMinutes: {
    webstore: 60 * 24 * 30, // 1 month
    rnr: 60 * 24, // 1 day
    code_base_rnr_portal: 60 * 24 * 1, // 1 day
    squid: 60 * 24 * 7, // 7 days
    gift_receiver: 30, // all gift receiver's end
    pluto: 60 * 24, // 1 day // sender's end
    smart_greeting: 30, // sender's end
    tp_cpn: 30, // 30 minutes
  },
  webstore: {
    paymentGateway: "custom" as paymentGateway,
  },
  isCommsEnabled: true,
};

const configs: {
  Prod: Config;
  Preprod: Config;
  Staging: Config;
  Local: Config;
} = {
  Prod: {
    ...defaultConfig,
    env: "Prod",
    baseUrl: "https://api.myhubble.money",
    baseDomainUrl: "https://www.myhubble.money",
    receiptUrl: "https://receipts.myhubble.money",
    enableErrorLoggingToSentry: true,
    razorpayKey: "rzp_live_RJ9FIsmeFPmxXS",
    bannerPath: "home_page/banners_web.json",
    showErrorMessageInErrorView: false,
    mixpanelEvents: {
      enabledForWebStore: true,
      enabledForSdk: false,
      enabledForRnr: true,
    },
    axisClientId: "axisbank-llE1XBk0",
    tdClientId: "tata-digital-23gvn314",
    tdSubscriptionRazorpayKey: "rzp_live_zNdS7cE9CK9DgA",
    customizedSDKClients: {
      limitedCategoryContent: ["axisbank-llE1XBk0", "bajajfinserv-CyyetFBG"],
      disableHomePageBackButton: [
        "visit-health-4AefioZfPjvOd51A",
        "gapup-gDM2WoUq",
        "stoofers-1F9eGgKR",
      ],
      consumeCoinsByDefault: ["gapup-gDM2WoUq"],
      squareAssetsForPopularBrands: [""],
      lowercaseTitleText: [""],
      trimCoinsOnAppHeader: ["policybazaar-6f4kMg4QM9L9"],
    },
    payUUrl: "https://secure.payu.in/_payment",
    isCommsEnabled: false,
  },
  Preprod: {
    ...defaultConfig,
    env: "Preprod",
    baseDomainUrl: "https://www.myhubble.money",
    baseUrl: "https://api.myhubble.money",
    receiptUrl: "https://receipts.dev.myhubble.money",
    enableErrorLoggingToSentry: true,
    razorpayKey: "rzp_live_RJ9FIsmeFPmxXS",
    bannerPath: "home_page/banners_web.json",
    showErrorMessageInErrorView: false,
    consoleLogging: {
      router: "disabled",
      fraudChecks: "enabled",
      serverRequests: "disabled",
      sdk: "disabled",
      middleware: ENABLE_MIDDLEWARE_LOGS,
      events: "disabled",
    },
    axisClientId: "axisbank-llE1XBk0",
    tdClientId: "tata-digital-23gvn314",
    tdSubscriptionRazorpayKey: "rzp_test_8J37geKQE76tyb",
    customizedSDKClients: {
      limitedCategoryContent: ["axisbank-llE1XBk0", "bajajfinserv-CyyetFBG"],

      disableHomePageBackButton: [
        "visit-health-4AefioZfPjvOd51A",
        "gapup-gDM2WoUq",
        "stoofers-1F9eGgKR",
      ],
      consumeCoinsByDefault: ["gapup-gDM2WoUq"],
      squareAssetsForPopularBrands: ["lazypay-ADKwqaBA"],
      lowercaseTitleText: [
        "lazypay-ADKwqaBA",
        "lazypay-mock-wY0rIcv4",
        "lazypay-mock",
      ],
      trimCoinsOnAppHeader: ["policybazaar-6f4kMg4QM9L9"],
    },
    payUUrl: "https://secure.payu.in/_payment",
  },
  Staging: {
    ...defaultConfig,
    env: "Staging",
    baseUrl: "https://api.dev.myhubble.money",
    baseDomainUrl: "https://vouchers.dev.myhubble.money",
    receiptUrl: "https://receipts.dev.myhubble.money",
    enableErrorLoggingToSentry: false,
    razorpayKey: "rzp_test_UaXfewLGPZYo8Y",
    bannerPath: "home_page/banners_web.staging.json",
    showErrorMessageInErrorView: true,
    consoleLogging: {
      router: "disabled",
      fraudChecks: "enabled",
      serverRequests: "disabled",
      sdk: "disabled",
      middleware: ENABLE_MIDDLEWARE_LOGS,
      events: "disabled",
    },
    axisClientId: "acme-code-3241c2c1",
    tdClientId: "tata-digital-23gvn314",
    tdSubscriptionRazorpayKey: "rzp_test_8J37geKQE76tyb",
    customizedSDKClients: {
      limitedCategoryContent: ["acme-code-3241c2c1", "bajaj-code-Bp3sxUCw"],
      disableHomePageBackButton: [
        "visit-health",
        "gapup-83yLRNIp",
        "stoofers-oPExjdmx",
      ],
      consumeCoinsByDefault: ["gapup-83yLRNIp"],
      squareAssetsForPopularBrands: [
        "lazypay-ADKwqaBA",
        "lazypay-mock-wY0rIcv4",
      ],
      lowercaseTitleText: [
        "lazypay-ADKwqaBA",
        "lazypay-mock-wY0rIcv4",
        "lazypay-mock",
      ],
      trimCoinsOnAppHeader: ["policybazaar-71c6c66d06aa"],
    },
    payUUrl: "https://test.payu.in/_payment",
    webstore: {
      paymentGateway: "razorpay" as paymentGateway,
    },
  },
  Local: {
    ...defaultConfig,
    env: "Local",
    baseUrl: "https://api.dev.myhubble.money",
    baseDomainUrl: "https://vouchers.dev.myhubble.money",
    receiptUrl: "https://receipts.dev.myhubble.money",
    enableErrorLoggingToSentry: false,
    razorpayKey: "rzp_test_UaXfewLGPZYo8Y",
    bannerPath: "home_page/banners_web.staging.json",
    showErrorMessageInErrorView: true,
    consoleLogging: {
      router: "disabled",
      fraudChecks: "enabled",
      serverRequests: "disabled",
      sdk: "enabled",
      middleware: ENABLE_MIDDLEWARE_LOGS,
      events: "disabled",
    },
    uiLogging: {
      loaderFromContext: "enabled",
    },
    axisClientId: "acme-code-3241c2c1",
    tdClientId: "tata-digital-23gvn314",
    tdSubscriptionRazorpayKey: "rzp_test_8J37geKQE76tyb",
    customizedSDKClients: {
      limitedCategoryContent: ["acme-code-3241c2c1", "bajaj-code-Bp3sxUCw"],
      disableHomePageBackButton: ["visit-health", "gapup-83yLRNIp"],
      consumeCoinsByDefault: ["gapup-83yLRNIp"],
      squareAssetsForPopularBrands: [
        "lazypay-ADKwqaBA",
        "lazypay-mock-wY0rIcv4",
      ],
      lowercaseTitleText: [
        "lazypay-ADKwqaBA",
        "lazypay-mock-wY0rIcv4",
        "lazypay-mock",
      ],
      trimCoinsOnAppHeader: ["policybazaar-71c6c66d06aa"],
    },
    payUUrl: "https://test.payu.in/_payment", //
  },
};

export const config = configs[env as Env];

export const HUBBLE_CLIENT_ID = "hubble";
